import {
  AddIcon,
  Box,
  Button,
  ChevronDownIcon,
  Flex,
  Link,
  List,
  Tag,
  TrashIcon,
  TypeAhead,
  Typography,
} from '@iheartjane/reefer';
import type {
  FontFamily,
  GoogleFontFamily,
  LabeledOption,
  Nullable,
} from '@iheartjane/types';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { bloomFontFamilyFromGoogleFontName } from '@jane/business-admin/util';
import { loadFont } from '@jane/shared/util';

import { FontImportModal } from './FontImportModal';
import { TextWithFont } from './TextWithFont';

import styles from './FontPicker.module.css';

const NO_CUSTOM_FONTS: FontFamily[] = [];

interface FontListItemProps {
  /** Name of the font family to display */
  displayName?: string;
  /** Underlying font family name to use in CSS rules */
  family: string;
  /** URL to the minimal font face needed to draw this font */
  fontUrl?: string;
  isDefault?: boolean;
  onClick: () => void;
  /** Given for the operator's own fonts, which they can drop from the list */
  onRemove?: () => void;
}

const FontListItem = ({
  displayName,
  family,
  fontUrl,
  isDefault,
  onClick,
  onRemove,
}: FontListItemProps) => {
  const label = displayName ?? family;

  return (
    <li>
      <InView
        onChange={(inView) =>
          inView && loadFont({ fontFamily: family, url: fontUrl })
        }
        triggerOnce
      >
        <Flex
          alignItems="center"
          className={styles['gray-hover']}
          borderRadius="xs"
          pr={onRemove ? 8 : undefined}
        >
          <button
            className={styles['font-list-item']}
            onClick={onClick}
            type="button"
          >
            <Flex alignItems="center" justifyContent="space-between" gap={8}>
              <TextWithFont fontFamily={family}>{label}</TextWithFont>
              {isDefault && (
                <Tag
                  size="sm"
                  color="grays-mid"
                  background="grays-ultralight"
                  label="Default"
                />
              )}
            </Flex>
          </button>

          {onRemove && (
            <Button.Icon
              ariaLabel={`Remove ${label} font`}
              className={styles['remove-font-button']}
              icon={<TrashIcon />}
              onClick={onRemove}
            />
          )}
        </Flex>
      </InView>
    </li>
  );
};

interface SelectedFontFieldProps {
  fontFamily: LabeledOption;
  isDefault?: boolean;
}

const SelectedFontField = ({
  isDefault,
  fontFamily: { label, value },
}: SelectedFontFieldProps) => {
  return (
    <Flex
      className={clsx(styles['selected-font-field'], styles['gray-hover'])}
      py={16}
      px={12}
      borderRadius="sm"
      border="grays-light"
      style={{
        '--font-family': value,
      }}
    >
      <Flex width="100%" justifyContent="space-between">
        {label}
        <Flex>
          {isDefault && (
            <Tag
              size="sm"
              color="grays-mid"
              background="grays-ultralight"
              label="Default"
              mr={12}
            />
          )}
          <ChevronDownIcon ml="auto" />
        </Flex>
      </Flex>
    </Flex>
  );
};

export interface FontPickerProps {
  activeFontFamily?: Nullable<LabeledOption>;
  customFontFamilies?: FontFamily[];
  defaultFontFamily: LabeledOption;
  disableMobileStyling?: boolean;
  enableCustomFonts?: boolean;
  fontFamilies: GoogleFontFamily[];
  isLayered?: boolean;
  label: string;
  limit?: number;
  maxHeight?: number;
  onChange: (activeFontFamily: Nullable<FontFamily>) => void;
  onRemoveCustomFont?: (font: FontFamily) => void;
}

export const FontPicker = ({
  activeFontFamily,
  customFontFamilies = NO_CUSTOM_FONTS,
  defaultFontFamily,
  disableMobileStyling,
  enableCustomFonts = false,
  fontFamilies,
  isLayered,
  label,
  limit = 100,
  maxHeight = 420,
  onChange,
  onRemoveCustomFont,
}: FontPickerProps) => {
  const [query, setQuery] = useState('');
  const [customFontModalOpen, setCustomFontModalOpen] = useState(false);

  const queryMatchesDefaultFamily = useMemo(
    () => defaultFontFamily.label.toLowerCase().includes(query.toLowerCase()),
    [defaultFontFamily, query]
  );
  const filteredCustomFontFamilies = useMemo(
    () =>
      customFontFamilies.filter(({ font_family }) =>
        font_family.toLowerCase().includes(query.toLowerCase())
      ),
    [customFontFamilies, query]
  );
  const filteredFontFamilies = useMemo(
    () =>
      fontFamilies.filter(({ family }) =>
        family.toLowerCase().includes(query.toLowerCase())
      ),
    [fontFamilies, query]
  );

  const activeGoogleFontUrl = useMemo(
    () =>
      fontFamilies.find(({ family }) => family === activeFontFamily?.value)
        ?.menu,
    [fontFamilies, activeFontFamily?.value]
  );

  useEffect(() => {
    loadFont({ fontFamily: activeFontFamily?.value, url: activeGoogleFontUrl });
  }, [activeFontFamily?.value, activeGoogleFontUrl]);

  useEffect(() => {
    customFontFamilies.forEach(({ font_family, url }) =>
      loadFont({ fontFamily: font_family, url })
    );
  }, [customFontFamilies]);

  return (
    <>
      <TypeAhead
        ariaLabel={label}
        disableMobileStyling={disableMobileStyling}
        isDebounced={false}
        isLayered={isLayered}
        placeholder="Search fonts"
        labelHidden
        listAriaLabel=""
        matchTargetWidth
        onChange={setQuery}
        query={query}
        customOptions={({ closePopover }) => (
          <Box maxHeight={maxHeight} overflowY="auto">
            {enableCustomFonts && (
              <Box
                // the typeahead adds 16px padding, but we want 12
                pt={-4}
                pb={12}
                px={16}
                mb={8}
                onClick={() => setCustomFontModalOpen(true)}
                aria-label="Import custom font"
              >
                <Flex alignItems="center" gap={8}>
                  <AddIcon color="primary" size="sm" m={4} />
                  <Link
                    color="primary"
                    onClick={() => setCustomFontModalOpen(true)}
                  >
                    Import custom font
                  </Link>
                </Flex>
              </Box>
            )}

            <Flex flexDirection="column" gap={8} px={16}>
              {enableCustomFonts && filteredCustomFontFamilies.length > 0 && (
                <>
                  <Typography variant="caps-regular" color="grays-mid">
                    Your fonts
                  </Typography>

                  <List label={`${label}-custom-list`}>
                    {filteredCustomFontFamilies.map((font) => (
                      <FontListItem
                        key={font.font_family}
                        onClick={() => {
                          onChange(font);
                          closePopover();
                        }}
                        family={font.font_family}
                        fontUrl={font.url}
                        onRemove={
                          onRemoveCustomFont && (() => onRemoveCustomFont(font))
                        }
                      />
                    ))}
                  </List>
                </>
              )}

              {queryMatchesDefaultFamily && (
                <>
                  <Typography variant="caps-regular" color="grays-mid">
                    Default
                  </Typography>

                  <List label={`${label}-default-list`}>
                    <FontListItem
                      onClick={() => {
                        onChange(null);
                        closePopover();
                      }}
                      isDefault
                      displayName={defaultFontFamily.label}
                      family={defaultFontFamily.value}
                    />
                  </List>
                </>
              )}

              {filteredFontFamilies.length > 0 && (
                <>
                  <Typography variant="caps-regular" color="grays-mid">
                    All Fonts
                  </Typography>

                  <List label={`${label}-all-fonts-list`}>
                    {filteredFontFamilies.slice(0, limit).map((font) => (
                      <FontListItem
                        key={font.family}
                        onClick={() => {
                          onChange(
                            bloomFontFamilyFromGoogleFontName(font.family) ??
                              null
                          );
                          closePopover();
                        }}
                        family={font.family}
                        fontUrl={font.menu}
                      />
                    ))}
                  </List>
                </>
              )}
            </Flex>
          </Box>
        )}
        width="100%"
        popoverTarget={
          <SelectedFontField
            isDefault={
              !activeFontFamily || activeFontFamily === defaultFontFamily
            }
            fontFamily={activeFontFamily ?? defaultFontFamily}
          />
        }
      />

      {enableCustomFonts && (
        <FontImportModal
          open={customFontModalOpen}
          onChange={onChange}
          onRequestClose={() => setCustomFontModalOpen(false)}
        />
      )}
    </>
  );
};
